import React, { useState, useEffect } from 'react';
import { UserValidator } from '@shoflo/polymer/validators/UserValidator/UserValidator';
import { PasswordInput } from '@shoflo/polymer/components/PasswordInput/PasswordInput';
import PropTypes from 'prop-types';
import { useFormState } from 'react-use-form-state';
import styled from 'styled-components';
import StyledFormLabel from 'components/form/StyledFormLabel';
import Notification from 'components/Notification';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

import { Row, Col, Input, Form, FormGroup, FormFeedback } from 'reactstrap';

import { Button } from '@shoflo/polymer/components/Button/Button';
const SmallText = styled.div`
  font-size: 11px;
  line-height: 16px;
  color: #767676;

  span {
    text-decoration: underline !important;
    color: #0095ff;
  }
`;

const NotificationWrapper = styled.div`
  margin-right: auto !important;
`;

const AccountSettings = ({
  user,
  onSubmit,
  onSendResetPassToken,
  onCancel,
  updateUserPasswordFailed
}) => {
  const [showResetNotification, setShowResetNotification] = useState(false);

  useEffect(() => {
    if (showResetNotification) {
      const timeoutId = setTimeout(() => {
        setShowResetNotification(false);
      }, 3000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [showResetNotification]);

  const getFormState = () => {
    if (!user) return {};
    const fullName = user ? `${user.firstName} ${user.lastName}` : '';
    return {
      fullName,
      email: user.email
    };
  };

  const [formState, { text, label }] = useFormState(getFormState());

  if (!user) return <span />;

  return (
    <Form
      onSubmit={e => {
        e.preventDefault();
        onSubmit(user._id, formState.values);
      }}
    >
      <FormGroup>
        <StyledFormLabel {...label('fullName')}>Name</StyledFormLabel>
        <Input
          id="fullName"
          aria-labelledby="fullName"
          {...text({
            name: 'fullName',
            validate: UserValidator.fullName,
            validateOnBlur: false,
            touchOnChange: true
          })}
          invalid={formState.touched.fullName && !formState.validity.fullName}
          required
          className="fs-exclude"
        />
        <FormFeedback>Invalid Full Name</FormFeedback>
      </FormGroup>

      <FormGroup>
        <StyledFormLabel htmlFor="email">Email</StyledFormLabel>
        <Input
          id="email"
          type="email"
          aria-labelledby="email"
          defaultValue={user.email}
          disabled
          className="fs-exclude"
        />
      </FormGroup>

      <Row>
        <Col sm={6}>
          <FormGroup>
            <StyledFormLabel htmlFor="password">
              Current password
            </StyledFormLabel>
            <Input
              {...text({
                name: 'currentPassword',
                validateOnBlur: false
              })}
              id="password"
              placeholder="Current Password"
              aria-labelledby="password"
              type="password"
              invalid={
                updateUserPasswordFailed &&
                updateUserPasswordFailed.status === 401 &&
                formState.touched.currentPassword
              }
              className="fs-exclude"
            />
            <FormFeedback>
              {`Uh oh! That's not your current password!`}
            </FormFeedback>
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup>
            <StyledFormLabel htmlFor="new_password">
              New password
            </StyledFormLabel>
            <PasswordInput
              {...text({
                name: 'newPassword',
                validateOnBlur: false
              })}
              id="new_password"
              placeholder="New Password"
              aria-labelledby="new_password"
              type="password"
              className="fs-exclude"
            />
          </FormGroup>
        </Col>
        <Col sm={12}>
          <SmallText style={{ marginTop: '-10px' }}>
            {`Don't remember? `}
            <span
              onClick={() => {
                onSendResetPassToken();
                setShowResetNotification(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              Send password reset email
            </span>
          </SmallText>
        </Col>
      </Row>
      <div className="modal-footer">
        <NotificationWrapper>
          {showResetNotification && (
            <Notification
              type="success"
              icon={faCheckCircle}
              message="A password reset email was sent!"
            />
          )}
        </NotificationWrapper>
        <div>
          <Button color="primary">Save</Button>{' '}
          {onCancel && (
            <Button onClick={onCancel} color="secondary" variant="outlined">
              Cancel
            </Button>
          )}
        </div>
      </div>
    </Form>
  );
};

AccountSettings.propTypes = {
  user: PropTypes.object.isRequired
};

export default AccountSettings;
